import * as React from 'react';

import { compose } from 'recompose';

import pendo, { LEARNER_API_KEY } from 'js/lib/pendo';
import user from 'js/lib/user';

import { DISABLE_PENDO_ORGS } from 'bundles/enterprise-legacy-learner-home/constants/ProgramHomeConstants';
import Naptime from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';
import { useEnterpriseInProgressCourse } from 'bundles/page-config-common/contexts/enterprise/EnterpriseEnrolledCourseContext';
import SimpleCourseraMetatags from 'bundles/seo/components/SimpleCourseraMetatags';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

type Props = {
  naptime: InjectedNaptime;
  children: React.ReactNode;
};

export const EnterprisePageTrackingWrapper = ({ children, naptime }: Props) => {
  const program = useProgram();
  const thirdPartyOrg = useThirdPartyOrganization();
  const programMembership = useProgramMembership();
  const [isProgramSwitcherUpdated, setIsProgramSwitcherUpdated] = React.useState(false);
  const [isFullStoryInitialized] = React.useState(false);
  const [isPendoInitialized, setIsPendoInitialized] = React.useState(false);
  const { inProgressCourse } = useEnterpriseInProgressCourse();

  React.useEffect(() => {
    // Update program switcher selection if user is member of program.
    if (user.isAuthenticatedUser() && programMembership.isProgramMember && !!program && !isProgramSwitcherUpdated) {
      naptime.executeMutation(
        ProgramSwitcherSelectionsV1.update(user.get().id, {
          selectionType: 'PROGRAM',
          programId: program?.id,
        })
      );
      setIsProgramSwitcherUpdated(true);
    }

    // Init pendo if enabled.
    if (!DISABLE_PENDO_ORGS.includes(thirdPartyOrg?.id || '') && !!program && !!thirdPartyOrg && !isPendoInitialized) {
      pendo.init(
        {
          visitor: user.isAuthenticatedUser()
            ? {
                id: user.get().external_id,
                isProgramAdmin: !!programMembership.isProgramAdmin,
                isProgramMember: !!programMembership.isProgramMember,
                isStaff: user.get().is_staff,
                isSuperuser: user.get().is_superuser,
                isSkillSetsEnabled: !!program?.metadata?.linkedTargetSkillProfileIds?.length,
                isCourserian: user.isCourserian(),
                programSlug: program?.metadata?.slug ?? '',
                hasCourseInProgress: !!inProgressCourse,
              }
            : {},
          account: {
            id: thirdPartyOrg?.id ?? '',
            name: thirdPartyOrg?.name ?? '',
            slug: thirdPartyOrg?.slug ?? '',
            isEnterprise: true,
            programSlug: program?.metadata?.slug ?? '',
            // This application doesn't serve career academy pages.
            isCareerAcademyV2: false,
          },
        },
        LEARNER_API_KEY
      );
      setIsPendoInitialized(true);
    }
  }, [
    program,
    thirdPartyOrg,
    programMembership,
    inProgressCourse,
    isProgramSwitcherUpdated,
    isFullStoryInitialized,
    isPendoInitialized,
    naptime,
  ]);

  return (
    <React.Fragment>
      {children}
      <SimpleCourseraMetatags disableCrawlerIndexing title={program?.metadata?.name} />
    </React.Fragment>
  );
};

/**
 * @description This wrapper is a bit of a catch-all for any tracking that should be published at the application level on page load
 *  Need naptime instance for enterprise save functionality.
 */
export default compose<Props, {}>(Naptime.createContainer(() => ({})))(EnterprisePageTrackingWrapper);
